/**
 * An account with no programme left to open — 0332.
 *
 * Platform staff can delete an operator (2.7b). Its affiliates can no longer
 * reach its programme, and the API answers every call from such a session
 * with NO_ACTIVE_PROGRAMME, naming the operator that closed. It still lets
 * three calls through: the person's live programmes, selecting one, and
 * signing out. This file decides what the page in place of the console says;
 * ClosedProgramme.tsx draws it.
 *
 * Kept free of JSX so node's test runner can load it.
 */

import { t } from './messages.ts';
import { selectableProgrammes } from './programmes.ts';

export const NO_ACTIVE_PROGRAMME = 'NO_ACTIVE_PROGRAMME';

/**
 * Raised on `window` by the client whenever a call answers
 * NO_ACTIVE_PROGRAMME, at sign-in or mid-use, and answered by the portal's
 * root, which swaps the console for the page. A window event for the reason
 * portal-kit's session-expired event is one: two copies of a module would
 * never hear each other's listeners.
 */
export const NO_ACTIVE_PROGRAMME_EVENT = 'affdice:no-active-programme';

export interface ClosedProgramme {
  /** The operator that closed, by its display name, or null when none did. */
  readonly closedOperator: string | null;
}

/**
 * Whether a failure is the NO_ACTIVE_PROGRAMME refusal, and if so what it
 * names. Matched by shape, not `instanceof`: Vite can load two copies of
 * portal-kit's ApiClientError.
 */
export function noActiveProgramme(error: unknown): ClosedProgramme | null {
  if (!error || typeof error !== 'object') return null;
  const e = error as { status?: unknown; code?: unknown; details?: unknown };
  if (e.status !== 403 || e.code !== NO_ACTIVE_PROGRAMME) return null;
  const named = (e.details as { closed_operator?: unknown } | undefined)?.closed_operator;
  return { closedOperator: typeof named === 'string' && named.trim() !== '' ? named : null };
}

/** The event's detail: what closed, and which request found out. */
export interface ClosedProgrammeEvent extends ClosedProgramme {
  readonly seq: number;
}

/*
 * Requests are numbered as they start. Choosing another programme opens a
 * new console, but the old console's requests may still be in flight and
 * answer NO_ACTIVE_PROGRAMME after the switch; the root notes the number at
 * the switch and ignores any refusal from a request started before it.
 */
let requestSeq = 0;

export function nextRequestSeq(): number {
  requestSeq += 1;
  return requestSeq;
}

export function currentRequestSeq(): number {
  return requestSeq;
}

/** True for a refusal from a request started before the programme switch. */
export function isStaleClosedEvent(event: { readonly seq?: number }, switchedAtSeq: number): boolean {
  return typeof event.seq === 'number' && event.seq <= switchedAtSeq;
}

export function notifyNoActiveProgramme(closed: ClosedProgramme, seq: number): void {
  const target = typeof globalThis.window !== 'undefined' ? globalThis.window : globalThis;
  if (typeof target.dispatchEvent !== 'function') return;
  const detail: ClosedProgrammeEvent = { closedOperator: closed.closedOperator, seq };
  target.dispatchEvent(new CustomEvent(NO_ACTIVE_PROGRAMME_EVENT, { detail }));
}

/** What `/v1/me/operators` answered, in the three states a load can be in. */
export type ProgrammesLoad =
  | { readonly state: 'loading' }
  | { readonly state: 'error' }
  | {
      readonly state: 'ready';
      readonly items: readonly {
        readonly operator_id: string;
        readonly display_name: string;
        readonly legal_name: string;
        readonly selectable: boolean;
      }[];
    };

export interface ProgrammeChoice {
  readonly operatorId: string;
  readonly label: string;
  /** The legal name, only when it says something the label does not. */
  readonly hint: string | null;
  /** Names the action for a screen reader: "Open Vela". */
  readonly action: string;
}

export type ClosedProgrammeView =
  | { readonly kind: 'loading'; readonly title: string }
  | {
      readonly kind: 'choose';
      readonly title: string;
      readonly lead: string;
      readonly records: string;
      readonly choices: readonly ProgrammeChoice[];
    }
  | {
      readonly kind: 'none';
      readonly title: string;
      /** Null when the lookup failed: the failure notice stands in for it. */
      readonly lead: string | null;
      readonly records: string;
      readonly steps: readonly string[];
      /** The lookup failed: say so and offer to try again. */
      readonly failed: boolean;
    };

/**
 * The page's words and choices.
 *
 * Two pages: another programme to open (choose it), or none (what the person
 * can do). A failed lookup shows the second with a retry, never the server's
 * text, and does not claim there is no other programme. With no operator named, the words name none: the selection ended
 * for another reason, and saying an operator "closed" would be a guess.
 */
export function closedProgrammeView(
  closed: ClosedProgramme,
  programmes: ProgrammesLoad,
): ClosedProgrammeView {
  const operator = closed.closedOperator;
  const named = operator !== null;
  const closedTitle = named ? t('closed.title.named', { operator }) : t('closed.title.unnamed');
  const records = named ? t('closed.records.named', { operator }) : t('closed.records.unnamed');

  if (programmes.state === 'loading') return { kind: 'loading', title: closedTitle };

  const choices = programmes.state === 'ready'
    ? selectableProgrammes(programmes.items).map((o) => ({
        operatorId: o.operator_id,
        label: o.display_name,
        hint: o.legal_name.trim() !== '' && o.legal_name.trim() !== o.display_name.trim()
          ? o.legal_name
          : null,
        action: t('closed.choose.open', { operator: o.display_name }),
      }))
    : [];

  if (choices.length > 0) {
    return {
      kind: 'choose',
      title: closedTitle,
      lead: named ? t('closed.choose.lead.named', { operator }) : t('closed.choose.lead.unnamed'),
      records: `${records} ${named ? t('closed.contact.named', { operator }) : t('closed.contact.unnamed')}`,
      choices,
    };
  }

  // A failed lookup cannot say there is no other programme, so it keeps the
  // closed title and says what it could not check.
  const failed = programmes.state === 'error';
  return {
    kind: 'none',
    title: failed ? closedTitle : t('closed.none.title'),
    // The page says what it could not check in its own notice, with the retry.
    lead: failed
      ? null
      : named ? t('closed.none.lead.named', { operator }) : t('closed.none.lead.unnamed'),
    records,
    steps: [
      named ? t('closed.contact.named', { operator }) : t('closed.contact.unnamed'),
      t('closed.none.join'),
    ],
    failed,
  };
}
